import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { frozenRateFields, rateOverrideFields, rateOverrideInputFields, rateOverridePatchFields, documentCurrencyInputFields, documentCurrencyPatchFields } from './fx.js';
import { DocumentLookupQuerySchema, DocumentLookupResponseSchema, DocumentNeighboursSchema, } from './document-navigation.js';
import { RatePctStringSchema, StatsCurrencyField } from './document-stats.js';
import { DocumentDateRangeQuery, pagedQuery, pagedResponse } from './pagination.js';
import { IsoDateSchema, MessageSchema, MoneyStringSchema, PctStringSchema, QtyStringSchema, UnitPrecisionSchema, } from './primitives.js';
import { ComponentTrackingResponseSchema, ComponentTrackingSchema, TwoWayAmountSchema, CommissionInputFields, CommissionPatchFields, CommissionResponseFields, } from './sales-invoices.js';
const c = initContract();
/**
 * Sales Returns / Credit Notes (PART 6, SAL-05 on the SAL-00 shared layout) —
 * the reversing document the invoices module was waiting for.
 *
 * Permission area: `sales`, per action:
 *   list/get = sales.view · create = sales.create · edit draft = sales.edit ·
 *   delete draft = sales.delete · POST RETURN = sales.approve (16.3's F/X:
 *   "whoever may put money into the books should be the one who may take it
 *   back out").
 *
 * The shape of a return: it is BUILT FROM a posted invoice (BR-SAL-05-02).
 * Every line derives from one billed line; prices, discounts and the tax
 * rate are COPIED, never typed (BR-SAL-05-04, BR-SAL-05-05) — the caller chooses only
 * WHICH lines and HOW MANY come back, capped by billed − already returned
 * (BR-SAL-05-03).
 *
 * RET-02 (owner decision 2026-10-12) adds a SECOND shape beside it, the
 * STAND-ALONE return: `invoiceId` null, a `partnerId` instead, and FREE
 * lines — an item, a quantity, a typed price (defaulting to the item's sale
 * price), a warehouse to restock into, and — wave 1B — an optional document
 * `discount` (F-2's two-way box, the invoice's own shape). It has no invoice
 * to cap against, so every cap rule above is simply not its subject; the
 * invoice-backed shape keeps every one of them untouched. Posting a
 * stand-alone return credits the receivable, puts the goods back at the cost
 * they LAST LEFT this customer at (the customer's most recent `sale`
 * movement for the item — an invoice's or a delivery note's), else the
 * location's current average, else the item's cost price, and taxes at the
 * company default (or the typed box), exactly as an invoice would. Posting
 * writes the opposite ledger entry: a FULL first
 * return reverses the invoice's entry through @palaccount/ledger
 * (reversal_of linkage); a PARTIAL return posts the proportional opposite
 * entry (DR revenue + VAT / CR receivables). Either way the customer owes
 * less and the trial balance nets (R9: posted only, ever).
 *
 * restock (BR-SAL-05-07) drives the §12 inventory/COGS legs — a restocking
 * return writes customer_return movements and the entry carries their
 * value. Refund-to-cash is the note's own `refund` route (SAL-05 §15 Q3's
 * explicit Refund action; §12 row 4's entry): DR 1121 / CR cash, stamped
 * refunded_at, once ever.
 */
// ── The fixed sentences of SAL-05 ───────────────────────────────────────────
export const CREDIT_MESSAGES = {
    /** BR-SAL-05-02 / ERR-SAL-05-01. */
    pickInvoice: 'Pick the invoice this return is against.',
    /** BR-SAL-05-03 / ERR-SAL-05-02, with the live numbers. */
    qtyCap: (billed, name, returned, left) => `The invoice billed ${billed} of ${name} and ${returned} have already come back. You can return at most ${left} more.`,
    /** BR-SAL-05-10 / ERR-SAL-05-03. */
    cancelledInvoice: (code) => `Invoice ${code} was cancelled. There is nothing to credit.`,
    /** BR-SAL-05-08 / ERR-SAL-05-04. */
    otherNeedsNote: 'Type a note explaining the reason for this return.',
    /** BR-SAL-05-04 / ERR-SAL-05-05. */
    pricesCopied: 'Prices on a return are copied from the original invoice and cannot be changed.',
    /** BR-SAL-05-06. */
    creditCap: 'A return cannot credit more than the invoice total, less credits already issued.',
    /** A return needs at least one line coming back. */
    noLines: 'Pick at least one line to return.',
    /** R8 family. */
    notADraft: 'Only a draft return can be posted.',
    postedLocked: 'This credit note is posted. Only the notes may change.',
    postedNoDelete: 'A posted credit note cannot be deleted.',
    /** §15 Q3's Refund action works on the posted credit, once. */
    refundOnlyPosted: 'Only a posted credit note can be refunded in cash.',
    alreadyRefunded: 'This credit note was already refunded in cash.',
    /** SAL-05 §12 row 4: the money leaves a cash or bank account. */
    refundAccountKind: 'A customer refund is paid from a cash or bank account.',
    /**
     * Wave 4A — applying the credit to invoices (the `apply` route). A draft
     * has no credit yet, a cancelled note none at all, and a refunded note's
     * credit left as cash (§12 row 4), so none of the three can be set against
     * an invoice.
     */
    applyNotPosted: 'Only a posted credit note can be applied to invoices.',
    applyAlreadyRefunded: 'This credit note was refunded in cash. Its credit no longer stands to be applied.',
    /** The target must be one of the customer's POSTED invoices (R9: a draft owes nothing yet). */
    applyInvoiceNotOpen: 'A credit note can only be applied to a posted invoice.',
    /** The per-note ceiling, with the live remainder (the purchase side's noteOverApplied, mirrored). */
    applyExceedsCredit: (remaining) => `This credit note has ${remaining} left to apply.`,
    /** The per-invoice ceiling, with the live balance (BR-FIN-03-06's shape for a credit). */
    applyExceedsInvoice: (code, open) => `Invoice ${code} has ${open} outstanding. A credit cannot settle more than that.`,
    /** A credit belongs to one customer's account and settles that customer's invoices only. */
    applyWrongCustomer: (code) => `Invoice ${code} belongs to another customer. A credit note settles its own customer's invoices only.`,
};
/**
 * D46 — SAL-05's parameterised credit-note refusals, structured. `qtyCap`'s
 * four params are PRE-FORMATTED quantity strings plus the item name; its key
 * `returns.errQtyCap` is SHARED with the sales-return editor's client
 * pre-check, which passes the same four params. `cancelledInvoice` carries an
 * invoice code. Wave 4A adds the three application refusals: a remainder, an
 * invoice code with its open balance, and an invoice code. No token, no
 * plural. Sentence parity pinned by `refusal-census.spec.ts`.
 */
export const CREDIT_REFUSALS = {
    qtyCap: (billed, name, returned, left) => ({
        message: CREDIT_MESSAGES.qtyCap(billed, name, returned, left),
        key: 'returns.errQtyCap',
        params: { billed, name, returned, left },
    }),
    cancelledInvoice: (code) => ({
        message: CREDIT_MESSAGES.cancelledInvoice(code),
        key: 'returns.errCancelledInvoice',
        params: { code },
    }),
    applyExceedsCredit: (remaining) => ({
        message: CREDIT_MESSAGES.applyExceedsCredit(remaining),
        key: 'returns.errApplyExceedsCredit',
        params: { remaining },
    }),
    applyExceedsInvoice: (code, open) => ({
        message: CREDIT_MESSAGES.applyExceedsInvoice(code, open),
        key: 'returns.errApplyExceedsInvoice',
        params: { code, open },
    }),
    applyWrongCustomer: (code) => ({
        message: CREDIT_MESSAGES.applyWrongCustomer(code),
        key: 'returns.errApplyWrongCustomer',
        params: { code },
    }),
};
/** SAL-05 §5 field 2 — the complete list of five. */
export const CreditReasonSchema = z.enum([
    'damaged_goods',
    'wrong_item_shipped',
    'customer_changed_mind',
    'overstock',
    'other',
]);
// ── Shapes ──────────────────────────────────────────────────────────────────
/**
 * One returning line: WHICH billed line and HOW MANY come back. Everything
 * else — description, price, discount — is copied from the invoice line
 * server-side (BR-SAL-05-04): the wire cannot even express a repriced return.
 */
export const InvoiceCreditLineInputSchema = z
    .object({
    /** The billed line's number on the invoice (1-based, SAL-00 §5d). */
    invoiceLineNo: z.number().int().positive(),
    qty: QtyStringSchema,
    /**
     * BR-MD-03-17 does not point one way: a tracked item names its
     * identifiers whichever direction it crosses the door. On a RESTOCKING
     * return these are what comes back — required to post, because units that
     * rejoin the shelf unnamed can never be sold again.
     */
    serialNos: z.string().trim().min(1).max(60).array().max(1000).optional(),
    batchNo: z.string().trim().min(1).max(40).optional(),
    /**
     * R7 in reverse (OQ-05-17's sub-table on the return). A kit went out as
     * its PARTS, so it comes back as its parts, and each part names its own
     * identifiers — a kit line has none of its own. Without this, every part
     * would be handed the line's `serialNos`, which is only ever right when
     * exactly one part is tracked.
     */
    components: ComponentTrackingSchema.array().max(200).optional(),
})
    .strict();
/**
 * RET-02 — a FREE line on a stand-alone return: the item that came back and
 * what to credit for it. `unitPrice` omitted = the item's sale price;
 * `description` omitted = the item's name; `warehouseId` is where it
 * restocks (required at posting when restock is on and the item moves
 * stock). Quantities are in the item's STOCK unit.
 */
export const FreeCreditLineInputSchema = z
    .object({
    itemId: z.uuid(),
    warehouseId: z.uuid().optional(),
    description: z.string().trim().min(1).max(200).optional(),
    qty: QtyStringSchema,
    unitPrice: MoneyStringSchema.optional(),
    discountPct: PctStringSchema.optional(),
    /**
     * Wave 4C: the unit `qty` was typed in — the item's stock unit or one of
     * its own Sell-allowed units; absent = the stock unit. The restock moves
     * the stock-unit conversion (BR-MD-03-04d).
     */
    unitId: z.uuid().optional(),
    serialNos: z.string().trim().min(1).max(60).array().max(1000).optional(),
    batchNo: z.string().trim().min(1).max(40).optional(),
    components: ComponentTrackingSchema.array().max(200).optional(),
})
    .strict();
/** A line is one shape or the other; a document is all of one shape. */
export const CreditLineInputSchema = z.union([
    InvoiceCreditLineInputSchema,
    FreeCreditLineInputSchema,
]);
export const CreditNoteLineSchema = z
    .object({
    lineNo: z.number().int().positive(),
    /** The billed line this returns — null on a stand-alone line (RET-02). */
    invoiceLineNo: z.number().int().positive().nullable(),
    /** Which catalogue item is coming back — null for a free-text line. */
    itemId: z.uuid().nullable(),
    /** RET-02: where a stand-alone line restocks to; null on an invoice-backed line. */
    warehouseId: z.uuid().nullable(),
    description: z.string(),
    /** What is coming back this time. */
    qty: z.string(),
    /** What the invoice billed on this line, for the editor's cap display; null on a stand-alone line. */
    billedQty: z.string().nullable(),
    /** Returned across OTHER posted credit notes (BR-SAL-05-03's "already"); null on a stand-alone line. */
    alreadyReturnedQty: z.string().nullable(),
    /**
     * `N-4` — the unit ALL THREE cap figures are in: billed, already
     * returned, and coming back now.
     *
     * It is the INVOICE LINE's unit, not the item's stock unit
     * (`BR-MD-03-04d`): `invoice_lines.qty` is stored in the unit the
     * salesperson typed, with `base_qty` beside it. A return of 2 boxes shown
     * as "2.000 kg" would be a wrong document, and the caps it is compared
     * against are that line's own numbers.
     *
     * Both null for a free-text line, which has no item and so no unit —
     * exactly as `InvoiceLineSchema` nulls its pair.
     */
    unitSymbol: z.string().nullable(),
    unitPrecision: UnitPrecisionSchema.nullable(),
    /**
     * Wave 4C: the typed unit behind `unitSymbol` — a stand-alone line's own
     * `unitId`, an invoice-backed line's billed unit; null = the item's stock
     * unit (or a free-text line).
     */
    unitId: z.uuid().nullable(),
    unitPrice: z.string(),
    discountPct: z.string(),
    lineTotal: z.string(),
    /** What came back, for a tracked item (BR-MD-03-17). */
    serialNos: z.string().array().nullable(),
    batchNo: z.string().nullable(),
    /** R7's sub-table as stored; empty for anything that is not a kit line. */
    components: ComponentTrackingResponseSchema.array(),
})
    .strict();
/**
 * Wave 4A adds `credited`: the note's FULL grand total has been applied to
 * invoices (the `apply` route, or `applyToInvoices` at posting). One way
 * only — an application is a posted fact. A cash-refunded note stays
 * `posted` with `refundedAt` stamped beside it (one status, not two).
 */
export const CreditNoteStatusSchema = z.enum(['draft', 'posted', 'credited', 'cancelled']);
/**
 * Wave 4A — one application of the note's credit to one invoice: a
 * credit-funded `receipt_allocations` row. It moves no money (the note's
 * SAL-05 §12 entry credited 1121 already; the invoice's receivable sits on
 * the same account) — it names WHICH invoice the standing credit settles.
 */
export const CreditNoteApplicationSchema = z
    .object({
    invoiceId: z.uuid(),
    invoiceCode: z.string(),
    amount: z.string(),
    appliedAt: z.string(),
})
    .strict();
export const CreditNoteSchema = z
    .object({
    id: z.uuid(),
    /** CN-#### in the number field itself (BR-SAL-05-09). */
    code: z.string(),
    creditNoteNo: z.number().int().positive(),
    status: CreditNoteStatusSchema,
    creditDate: IsoDateSchema,
    partnerId: z.uuid(),
    partnerCode: z.string(),
    partnerName: z.string(),
    /** The strip pill: "Against invoice · INV-####" — clicks through. Both null on a stand-alone return (RET-02). */
    invoiceId: z.uuid().nullable(),
    invoiceCode: z.string().nullable(),
    reason: CreditReasonSchema,
    reasonNote: z.string().nullable(),
    /** BR-SAL-05-07 — damaged goods must not restock; ticked, they return. */
    restock: z.boolean(),
    currency: z.string(),
    /** EC-19-10: the rate this document froze when it was confirmed, and its "as of" date. */
    ...frozenRateFields,
    /** Wave 4D — the typed-rate flag and the market hint (fx.ts). */
    ...rateOverrideFields,
    /** Wave 4D — the sales commission (sales-invoices.ts). */
    ...CommissionResponseFields,
    lines: CreditNoteLineSchema.array(),
    subtotal: z.string(),
    discountPct: z.string(),
    discountAmount: z.string(),
    taxPct: z.string(),
    taxAmount: z.string(),
    grandTotal: z.string(),
    notes: z.string().nullable(),
    journalEntryId: z.uuid().nullable(),
    postedAt: z.string().nullable(),
    /** §15 Q3's Refund stamp pair — set once by the refund action. */
    refundedAt: z.string().nullable(),
    refundJournalEntryId: z.uuid().nullable(),
    /**
     * Wave 4A — Σ `applications` (what the credit has been set against) and
     * grandTotal − that (what still stands on the customer's account, free
     * to apply or refund). A cash refund pays out the UNAPPLIED remainder
     * only, so after one `unappliedTotal` is what left as cash. A LINKED
     * note with NO applications nets its own invoice automatically (wave 1B)
     * — its `unappliedTotal` is its whole value and the invoice screens
     * already show it netted; the first explicit application replaces that
     * automatic netting (`invoice-settlement.ts` states the rule).
     */
    appliedTotal: z.string(),
    unappliedTotal: z.string(),
    applications: CreditNoteApplicationSchema.array(),
    createdAt: z.string(),
})
    .strict();
/**
 * Wave 3B — the returns list's tiles over the WHOLE register; the shape
 * every `/stats` route shares is explained in `document-stats.ts`. Defined
 * here rather than there because it names `CreditReasonSchema`.
 */
export const CreditNoteStatsSchema = z
    .object({
    /** Σ grand totals of POSTED credit notes dated this calendar month, in base. */
    returnsThisMonthValue: MoneyStringSchema,
    returnsThisMonthCount: z.number().int().min(0),
    /** Posted, not refunded in cash, and not standing against an invoice (stand-alone). */
    awaitingCreditOrRefund: z.number().int().min(0),
    /** Posted returns value ÷ posted invoiced value over the last 90 days; "0.00" when nothing was invoiced. */
    returnRatePct: RatePctStringSchema,
    ...StatsCurrencyField,
})
    .strict();
export const RefundCreditNoteRequestSchema = z
    .object({
    /** SAL-05 §12 row 4: the cash/bank account the refund is paid from. */
    paidFromAccountId: z.uuid(),
})
    .strict();
/**
 * Wave 4A — set the posted note's standing credit against named invoices.
 * Each row is one invoice and a positive Money string; the same invoice may
 * not appear twice in one call (apply once, with the sum). Capped per call
 * at the note's `unappliedTotal` and per row at the invoice's open balance,
 * both within half a cent (BR-FIN-03-15's tolerance, the receipts' own).
 */
export const ApplyCreditNoteRequestSchema = z
    .object({
    allocations: z
        .object({ invoiceId: z.uuid(), amount: MoneyStringSchema })
        .strict()
        .array()
        .min(1)
        .max(200),
})
    .strict()
    .refine((body) => new Set(body.allocations.map((row) => row.invoiceId)).size === body.allocations.length, { message: 'Each invoice appears once in an application.', path: ['allocations'] });
/**
 * Wave 4A — Post return's one option. `applyToInvoices` on a STAND-ALONE
 * return (RET-02) sets the credit against the customer's open invoices
 * oldest first (BR-FIN-03-10's walk, the receipts' own helper) in the same
 * transaction as the posting; whatever the invoices cannot absorb stays on
 * the account as unapplied credit. Ignored on an invoice-backed return,
 * whose credit already nets its own invoice.
 */
export const PostCreditNoteRequestSchema = z
    .object({ applyToInvoices: z.boolean().optional() })
    .strict();
export const CreateCreditNoteRequestSchema = z
    .object({
    /** Wave 4D — a typed exchange rate on the draft (fx.ts). */
    ...rateOverrideInputFields,
    /** Per-document currency (fx.ts documentCurrencyInputFields). */
    ...documentCurrencyInputFields,
    /** Wave 4D — the sales commission % (sales-invoices.ts). */
    ...CommissionInputFields,
    /** BR-SAL-05-02: the posted invoice this return is against. Omitted = a STAND-ALONE return (RET-02). */
    invoiceId: z.uuid().optional(),
    /** RET-02: the customer a stand-alone return credits. Ignored (the invoice's) when `invoiceId` is given. */
    partnerId: z.uuid().optional(),
    creditDate: IsoDateSchema,
    reason: CreditReasonSchema,
    reasonNote: z.string().trim().max(500).optional(),
    restock: z.boolean().optional(),
    /**
     * Invoice-backed: omitted = a FULL return, every billed line at its full
     * remaining qty. Stand-alone: required, free lines.
     */
    lines: CreditLineInputSchema.array().min(1).optional(),
    /** RET-02: F-2's document discount on a stand-alone return (pct or amount, the invoice's own shape); omitted = none. Invoice-backed returns copy the invoice's. */
    discount: TwoWayAmountSchema.optional(),
    /** RET-02: F-4's VAT box on a stand-alone return; omitted = the company default rate. Invoice-backed returns copy the invoice's. */
    tax: TwoWayAmountSchema.optional(),
    notes: z.string().max(2000).optional(),
})
    .strict()
    .refine((body) => body.invoiceId !== undefined || body.partnerId !== undefined, {
    message: 'A return names the invoice it is against, or the customer it credits.',
    path: ['partnerId'],
})
    .refine((body) => body.invoiceId !== undefined || (body.lines?.length ?? 0) > 0, {
    message: 'A stand-alone return needs at least one line.',
    path: ['lines'],
});
export const UpdateCreditNoteRequestSchema = z
    .object({
    /** Wave 4D — a typed exchange rate on the draft (fx.ts). */
    ...rateOverridePatchFields,
    /** Per-document currency, draft only (fx.ts documentCurrencyPatchFields). */
    ...documentCurrencyPatchFields,
    /** Wave 4D — the sales commission % (sales-invoices.ts). */
    ...CommissionPatchFields,
    creditDate: IsoDateSchema.optional(),
    reason: CreditReasonSchema.optional(),
    reasonNote: z.string().trim().max(500).nullable().optional(),
    restock: z.boolean().optional(),
    /** Present = replace the returning set (draft only). */
    lines: CreditLineInputSchema.array().min(1).optional(),
    /** RET-02: the document discount of a stand-alone draft (kept as typed unless re-typed). */
    discount: TwoWayAmountSchema.optional(),
    /** RET-02: the VAT box of a stand-alone draft. */
    tax: TwoWayAmountSchema.optional(),
    /** The ONE field editable after posting (BR-SAL-00-22). */
    notes: z.string().max(2000).nullable().optional(),
})
    .strict();
const creditNoteParams = z.object({ creditNoteId: z.uuid() }).strict();
export const creditNotesContract = c.router({
    list: {
        method: 'GET',
        path: '/api/sales/credit-notes',
        query: pagedQuery({
            partnerId: z.uuid().optional(),
            invoiceId: z.uuid().optional(),
            status: CreditNoteStatusSchema.optional(),
            /** GEN-50 / the header rail's Find: the printed number or the customer's name. */
            q: z.string().trim().max(120).optional(),
            /** Wave 3B — bounds on the credit date, both inclusive. */
            ...DocumentDateRangeQuery,
        }),
        responses: {
            200: pagedResponse('creditNotes', CreditNoteSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Credit notes (sales.view), PAGED (NFR-42) — all three filters in SQL. Drafts appear here and nowhere else (R9).',
    },
    get: {
        method: 'GET',
        path: '/api/sales/credit-notes/:creditNoteId',
        pathParams: creditNoteParams,
        responses: {
            200: CreditNoteSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One credit note with its lines and caps (sales.view).',
    },
    /**
     * S4 — the document header's ‹‹ ›› rail (wave 2 of specs/erp-gap-analysis.md):
     * the credit notes on either side of this one in number order (null at an
     * end). See `document-navigation.ts`.
     */
    neighbours: {
        method: 'GET',
        path: '/api/sales/credit-notes/:creditNoteId/neighbours',
        pathParams: creditNoteParams,
        responses: {
            200: DocumentNeighboursSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'The previous and next credit note by number (sales.view).',
    },
    /** S4 — the "Go to no." box: one credit note by its printed number; 404 on a miss. */
    lookup: {
        method: 'GET',
        path: '/api/sales/credit-notes/lookup',
        query: DocumentLookupQuerySchema,
        responses: {
            200: DocumentLookupResponseSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One credit note by its printed number (sales.view).',
    },
    create: {
        method: 'POST',
        path: '/api/sales/credit-notes',
        body: CreateCreditNoteRequestSchema,
        responses: {
            201: CreditNoteSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'New draft return (sales.create). Against a POSTED invoice: lines loaded from the invoice with billed and already-returned quantities (SAL-05 §16), prices copied (BR-SAL-05-04), caps applied (BR-SAL-05-03). STAND-ALONE (RET-02, no invoiceId): a customer and free lines priced from the item (editable), no caps.',
    },
    update: {
        method: 'PATCH',
        path: '/api/sales/credit-notes/:creditNoteId',
        pathParams: creditNoteParams,
        body: UpdateCreditNoteRequestSchema,
        responses: {
            200: CreditNoteSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit a DRAFT (sales.edit); a posted credit note answers 409 except a notes-only change (R8, BR-SAL-00-22).',
    },
    remove: {
        method: 'DELETE',
        path: '/api/sales/credit-notes/:creditNoteId',
        pathParams: creditNoteParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete a DRAFT (sales.delete); posted answers 409 (BR-SAL-00-23).',
    },
    post: {
        method: 'POST',
        path: '/api/sales/credit-notes/:creditNoteId/post',
        pathParams: creditNoteParams,
        body: PostCreditNoteRequestSchema.optional(),
        responses: {
            200: CreditNoteSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Post return (sales.approve) — Draft → Posted: writes the OPPOSITE of the invoice entry through the ledger in ONE transaction (full first return = reverse-linked via reversal_of; partial = proportional mirror: DR revenue + VAT / CR receivables), reduces the customer balance, locks the document. `applyToInvoices` (wave 4A, stand-alone returns): also sets the credit against the customer\'s open invoices oldest first.',
    },
    apply: {
        method: 'POST',
        path: '/api/sales/credit-notes/:creditNoteId/apply',
        pathParams: creditNoteParams,
        body: ApplyCreditNoteRequestSchema,
        responses: {
            200: CreditNoteSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: "Apply the credit to invoices (wave 4A; sales.approve — the act that decides which receivable the credit settles, the same hand that posted it). Inserts credit-funded allocation rows and posts NO journal entry: the credit and the invoices' receivables both sit on 1121 already, so the ledger is unchanged and only the invoice balances move. Posted, un-refunded notes only; capped at the note's unapplied remainder and each invoice's open balance; same customer only. Flips the note to `credited` when its full value is applied. Audited (credit_note.apply, credit_note.credit).",
    },
    refund: {
        method: 'POST',
        path: '/api/sales/credit-notes/:creditNoteId/refund',
        pathParams: creditNoteParams,
        body: RefundCreditNoteRequestSchema,
        responses: {
            200: CreditNoteSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: "Refund the credit in cash (accounting.approve — money out is the treasurer's act): DR 1121 Trade Receivables / CR the chosen cash/bank account at the note's grand total (SAL-05 §12 row 4 — the credit stops standing against the balance and leaves as money), the GEN-113 exchange difference on the refund date (§15: the note copied the invoice's rate; 'a cash refund at another rate does create one'). Posted notes only; once ever; stamped (refunded_at) and audited (credit_note.refund).",
    },
    /** Wave 3B — the list's tiles over the WHOLE register (see `document-stats.ts`). */
    stats: {
        method: 'GET',
        path: '/api/sales/credit-notes/stats',
        responses: {
            200: CreditNoteStatsSchema,
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'SAL-05 list tiles (sales.view): returns this month (value, count), awaiting credit or refund, return rate over 90 days — posted-only, base currency, in SQL.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=credit-notes.js.map